import Vue from 'vue'

// Toast Notification Component Styles
import 'vue-toastification/dist/index.css'
import '@core/scss/vue/libs/toastification.scss'

const defaultOptions = {
  timeout: 3000,
}

const activeToasts = new Map()
let toastId = 0

const typeMap = {
  danger: 'error',
  failed: 'error',
  primary: 'default',
  secondary: 'default',
}

function nextToastId() {
  toastId += 1
  return toastId
}

function normalizeType(variant) {
  const type = typeMap[variant] || variant || 'default'
  if (['default', 'info', 'success', 'warning', 'error'].includes(type)) return type
  return 'default'
}

function normalizeContent(content, options) {
  const props = content && typeof content === 'object' ? content.props || {} : {}
  const isTextToast = typeof content === 'string'
  const variant = options.type || props.variant || 'default'
  const timeout = Object.prototype.hasOwnProperty.call(options, 'timeout') ? options.timeout : defaultOptions.timeout

  return {
    title: isTextToast ? content : props.title,
    text: props.text,
    timeout,
    type: normalizeType(variant),
    variant,
  }
}

function ensureContainer() {
  let container = document.querySelector('.Vue-Toastification__container.top-right')

  if (!container) {
    container = document.createElement('div')
    container.className = 'Vue-Toastification__container top-right'
    document.body.appendChild(container)
  }

  return container
}

function dismissToast(id) {
  const toast = activeToasts.get(id)
  if (!toast) return

  activeToasts.delete(id)
  toast.remove()
}

function createToastElement(id, content) {
  const toast = document.createElement('div')
  toast.className = `Vue-Toastification__toast Vue-Toastification__toast--${content.type} top-right`
  toast.setAttribute('role', 'alert')

  const body = document.createElement('div')
  body.className = 'Vue-Toastification__toast-body'

  if (content.title) {
    const title = document.createElement('div')
    title.className = `font-weight-bolder toastification-title text-${content.variant}`
    title.textContent = content.title
    body.appendChild(title)
  }

  if (content.text) {
    const text = document.createElement('small')
    text.className = 'd-inline-block text-body'
    text.textContent = content.text
    body.appendChild(text)
  }

  const closeButton = document.createElement('button')
  closeButton.type = 'button'
  closeButton.className = 'Vue-Toastification__close-button'
  closeButton.setAttribute('aria-label', 'close')
  closeButton.textContent = 'x'
  closeButton.addEventListener('click', () => dismissToast(id))

  toast.appendChild(body)
  toast.appendChild(closeButton)

  return toast
}

function showToast(content, options = {}) {
  if (typeof document === 'undefined') return null

  const id = nextToastId()
  const normalizedContent = normalizeContent(content, options)
  const toast = createToastElement(id, normalizedContent)

  ensureContainer().appendChild(toast)
  activeToasts.set(id, toast)

  if (normalizedContent.timeout !== false) {
    window.setTimeout(() => dismissToast(id), Number(normalizedContent.timeout) || defaultOptions.timeout)
  }

  return id
}

showToast.clear = () => {
  Array.from(activeToasts.keys()).forEach(dismissToast)
}

showToast.dismiss = dismissToast
showToast.updateDefaults = () => {}
showToast.update = (id, toast) => {
  dismissToast(id)
  return showToast(toast.content, toast.options || {})
}

showToast.success = (content, options = {}) => showToast(content, { ...options, type: 'success' })
showToast.info = (content, options = {}) => showToast(content, { ...options, type: 'info' })
showToast.warning = (content, options = {}) => showToast(content, { ...options, type: 'warning' })
showToast.error = (content, options = {}) => showToast(content, { ...options, type: 'error' })

Vue.prototype.$toast = showToast
